import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getProduct } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { STORE } from "@/lib/store";
import NotFound from "@/pages/NotFound";

/**
 * All internal product routes now redirect straight to the Shift4Shop product
 * page. Renova acts as a marketing front — checkout, cart, inventory and
 * payments all live on the connected store.
 */
export default function Product() {
  const { slug } = useParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => getProduct(slug),
    retry: false,
  });

  const product = data?.product;
  const target = product?.store_url || STORE.shop;

  useEffect(() => {
    if (product) window.location.replace(target);
  }, [product, target]);

  useSEO({
    title: product ? `${product.name} | Renova Peptide Co.` : "Product | Renova Peptide Co.",
    description: product ? `${product.name} — ${product.short}. For laboratory research use only.` : "",
    path: `/product/${slug}`,
    noindex: true,
  });

  if (isError) return <NotFound />;

  return (
    <div className="container-rnv py-32 text-center" data-testid="product-redirect">
      <div className="inline-flex items-center gap-3 text-sm text-[#85827D] uppercase tracking-[0.22em]">
        <span className="h-px w-8 bg-[#C8A96A]" />
        Taking you to our secure store
        <span className="h-px w-8 bg-[#C8A96A]" />
      </div>
      <p className="font-serif text-3xl text-[#1E2A44] mt-4">
        {isLoading ? "Loading product…" : "Redirecting…"}
      </p>
      {!isLoading && (
        <p className="mt-3 text-sm text-[#85827D]">
          If you are not redirected automatically,{" "}
          <a href={target} className="text-[#1E2A44] underline" data-testid="product-redirect-link">
            click here to continue on our store
          </a>
          .
        </p>
      )}
    </div>
  );
}
